import { useEffect, useState } from "react";
import { ExternalLink, Globe, Loader2, Unplug } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const field = "w-full rounded-lg border border-white/10 bg-[#07090E] px-3 py-2 text-sm text-white outline-none focus:border-cyan-400/60";
const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);

export const PublishPanel = ({ project, versions, onChange }) => {
  const pub = project.published;
  const [slug, setSlug] = useState(pub?.slug || slugify(project.name));
  const [version, setVersion] = useState(pub?.version || project.current_version);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!pub) setVersion(project.current_version); }, [project.current_version, pub]);
  const link = pub ? `${window.location.origin}/sites/${pub.slug}` : "";

  const run = async (fn, ok) => {
    setBusy(true); setError("");
    try { await fn(); toast.success(ok); await onChange(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const publish = (e) => { e.preventDefault(); run(() => api.post(`/admin/studio/projects/${project.id}/publish`, { slug, version: Number(version) }), `Published v${version}`); };
  const unpublish = () => run(() => api.delete(`/admin/studio/projects/${project.id}/publish`), "Site unpublished");

  return (
    <form onSubmit={publish} data-testid="studio-publish-panel" className="card-tech space-y-3 rounded-2xl p-4">
      <p className="flex items-center gap-2 text-sm font-semibold text-white"><Globe className="h-4 w-4 text-cyan-300" /> Publish</p>
      {pub && (
        <div data-testid="studio-published-info" className="rounded-xl border border-emerald-400/30 bg-emerald-400/5 p-3 text-xs">
          <p className="text-emerald-300">Live · showing v{pub.version}</p>
          <a href={link} target="_blank" rel="noreferrer" data-testid="studio-published-link" className="mt-1 inline-flex items-center gap-1 break-all text-cyan-300 hover:text-white">{link} <ExternalLink className="h-3 w-3 shrink-0" /></a>
        </div>
      )}
      <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
        <label className="text-xs text-slate-400">Link
          <div className="mt-1 flex items-center rounded-lg border border-white/10 bg-[#07090E] pl-3 text-sm text-slate-500 focus-within:border-cyan-400/60">
            /sites/<input value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="studio-publish-slug-input" className="w-full bg-transparent py-2 pr-3 text-white outline-none" />
          </div>
        </label>
        <label className="text-xs text-slate-400">Version
          <select value={version} onChange={(e) => setVersion(e.target.value)} data-testid="studio-publish-version-select" className={`${field} mt-1`}>
            {versions.map((v) => <option key={v.number} value={v.number} className="bg-[#0B0F18]">v{v.number}</option>)}
          </select>
        </label>
      </div>
      {error && <p data-testid="studio-publish-error" className="text-xs text-red-400">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="submit" disabled={busy || !slug.trim()} data-testid="studio-publish-button" className="btn-glow inline-flex items-center gap-2 rounded-full bg-cyan-400 px-4 py-2 text-sm font-semibold text-[#07090E] hover:bg-cyan-300 disabled:opacity-40">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {pub ? "Update link" : "Publish"}
        </button>
        {pub && <button type="button" disabled={busy} onClick={unpublish} data-testid="studio-unpublish-button" className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-slate-200 hover:border-red-400/50"><Unplug className="h-4 w-4" /> Unpublish</button>}
      </div>
    </form>
  );
};
